// Product Catalog admin page — Milestone 1 of the Material Calculator.
// Three stacked sections: Calculator Settings (markup/handling),
// Products (master price list with CSV import), and Systems (the 18 named
// roofing assemblies). Recipe editor (which products go into each system at
// what coverage rate) opens in a side-drawer when you tap a system row.
//
// Endpoints used:
//   GET/POST/PATCH/DELETE /api/products
//   POST                  /api/products/import-csv
//   GET/POST/PATCH/DELETE /api/systems
//   GET/PUT               /api/systems/{id}/recipe
//   GET/PUT               /api/calculator/settings
import Materials from "@/pages/Materials";
import { PriceLists } from "@/pages/SinglePly";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Package, Layers, Settings, ChevronRight, ChevronDown, ChevronUp, Pencil, Save, X, Search, Boxes } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("crm_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const CATEGORIES = ["FARM", "Acrylic", "Silicone", "Fabric", "Mastic", "TPO", "EPDM", "ModBit", "PVC", "Other"];
const UNITS = ["gal", "pail", "roll", "sf", "lf", "ea", "bag", "tube", "box"];
const COVERAGE_BASIS = [
  { value: "per_100sf", label: "per 100 sf" },
  { value: "per_sf",    label: "per sf" },
  { value: "per_lf",    label: "per linear ft" },
  { value: "per_each_optional", label: "user enters qty" },
];

/**
 * Explicit brand tones for the vendors currently in the catalog. Anything
 * not in this map hashes into one of the fallback tones so new vendors
 * still get *a* color (stable per name) without a code change.
 * Keys are matched case-insensitively.
 */
const VENDOR_TONES = {
  "everest systems":       { chip: "bg-blue-600 text-white",       accent: "border-l-blue-600 bg-blue-50/40" },
  "western colloid":       { chip: "bg-orange-600 text-white",     accent: "border-l-orange-600 bg-orange-50/40" },
  "gaco":                  { chip: "bg-emerald-600 text-white",    accent: "border-l-emerald-600 bg-emerald-50/40" },
  "sesco":                 { chip: "bg-violet-600 text-white",     accent: "border-l-violet-600 bg-violet-50/40" },
  "national waterproofing":{ chip: "bg-sky-600 text-white",        accent: "border-l-sky-600 bg-sky-50/40" },
  "gaf":                   { chip: "bg-red-600 text-white",        accent: "border-l-red-600 bg-red-50/40" },
  "carlisle":              { chip: "bg-amber-600 text-white",      accent: "border-l-amber-600 bg-amber-50/40" },
  "firestone":             { chip: "bg-rose-600 text-white",       accent: "border-l-rose-600 bg-rose-50/40" },
  "tremco":                { chip: "bg-teal-600 text-white",       accent: "border-l-teal-600 bg-teal-50/40" },
};
const FALLBACK_TONES = [
  { chip: "bg-indigo-600 text-white",  accent: "border-l-indigo-600 bg-indigo-50/40" },
  { chip: "bg-cyan-600 text-white",    accent: "border-l-cyan-600 bg-cyan-50/40" },
  { chip: "bg-lime-600 text-white",    accent: "border-l-lime-600 bg-lime-50/40" },
  { chip: "bg-fuchsia-600 text-white", accent: "border-l-fuchsia-600 bg-fuchsia-50/40" },
  { chip: "bg-stone-700 text-white",   accent: "border-l-stone-700 bg-stone-50" },
];
const NO_VENDOR_TONE = { chip: "bg-zinc-400 text-white", accent: "border-l-zinc-300 bg-zinc-50" };
const vendorTone = (raw) => {
  const v = (raw || "").trim();
  if (!v || v === "(No vendor)") return NO_VENDOR_TONE;
  const explicit = VENDOR_TONES[v.toLowerCase()];
  if (explicit) return explicit;
  // Stable fallback: sum char codes → index into FALLBACK_TONES. Keeps the
  // same vendor coloured the same way across page reloads.
  let hash = 0;
  for (let i = 0; i < v.length; i += 1) hash = (hash * 31 + v.charCodeAt(i)) | 0;
  return FALLBACK_TONES[Math.abs(hash) % FALLBACK_TONES.length];
};

const inputCls = "border border-zinc-300 px-3 h-9 text-sm w-full focus:outline-none focus:border-blue-700";
const labelCls = "text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1";

export default function ProductCatalog() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  // Deep-link support: /catalog?tab=by-vendor lands directly on the
  // Materials-by-Vendor tab (old /materials bookmarks redirect here).
  const initialTab = sp.get("tab");
  const validTabs = ["products", "by-vendor", "systems", "settings"];
  const [tab, setTab] = useState(validTabs.includes(initialTab) ? initialTab : "products");
  useEffect(() => {
    if (tab !== "products") {
      setSp({ tab }, { replace: true });
    } else if (sp.get("tab")) {
      const next = new URLSearchParams(sp);
      next.delete("tab");
      setSp(next, { replace: true });
    }
  }, [tab, sp, setSp]);
  // Sales reps see individual unit pricing here (Products tab lists every
  // vendor product with its unit_price), which is not appropriate. Bounce
  // them to the Calculator where they can build system totals without
  // ever seeing raw wholesale numbers. Redirect happens AFTER hooks so
  // rules-of-hooks are respected.
  if (user && user.role === "sales") return <Navigate to="/calculator" replace />;
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="product-catalog-page">
      <header>
        <h1 className="text-2xl font-black tracking-tight">Product Catalog &amp; Roofing Systems</h1>
        <p className="text-sm text-zinc-600 mt-1">
          Master price list, named system assemblies, and the shipping / handling-fee defaults that drive the Material Calculator.
        </p>
      </header>
      <div className="flex gap-1 border-b border-zinc-200">
        {[
          ["products", "Fluid Applied", Package],
          ["traditional", "Traditional Roofing", Boxes],
          ["by-vendor", "Materials by Vendor", Boxes],
          ["systems", "Systems", Layers],
          ["settings", "Calculator Settings", Settings],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
              tab === k ? "border-blue-700 text-blue-700" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
            data-testid={`tab-${k}`}
          >
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>
      {tab === "traditional" && (
        <div className="pt-6">
          <p className="text-xs text-zinc-500 mb-4 -mt-2">
            Distributor price lists for TPO / EPDM / PVC / ModBit. To build a takeoff and pricing from these,
            use the <Link to="/single-ply" className="text-blue-700 font-bold hover:underline" data-testid="link-traditional-estimator">Traditional Estimator</Link>.
          </p>
          <PriceLists />
        </div>
      )}
      {tab === "products" && <ProductsTab />}
      {tab === "by-vendor" && <Materials embedded />}
      {tab === "systems" && <SystemsTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

// ───────────────────────────────────────── PRODUCTS ──────────────────────
function ProductsTab() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  // UI state: search box + which manufacturer sections are expanded.
  // Default all vendors expanded so a first-time visitor sees the full
  // catalog. Collapsing state is per-vendor and persists in local state.
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const load = async () => {
    try { setRows((await api.get("/products")).data); }
    catch { toast.error("Failed to load products"); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (p) => { setEditingId(p.id); setDraft({ ...p }); };
  const cancelEdit = () => { setEditingId(null); setDraft({}); };
  const saveEdit = async () => {
    try {
      await api.patch(`/products/${editingId}`, draft);
      toast.success("Saved");
      cancelEdit(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const removeProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Recipes referencing it will keep their reference but the product won't show in pickers.`)) return;
    await api.delete(`/products/${p.id}`);
    load();
  };
  // Move a product up/down within its vendor group and persist the order.
  const moveProduct = async (vendorItems, index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= vendorItems.length) return;
    const ordered = [...vendorItems];
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    // Optimistic: rewrite sort_order locally so the row moves instantly.
    const orderMap = Object.fromEntries(ordered.map((p, i) => [p.id, i]));
    setRows((rs) => rs.map((r) => (r.id in orderMap ? { ...r, sort_order: orderMap[r.id] } : r)));
    try { await api.post("/products/reorder", { ids: ordered.map((p) => p.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  // Group products by vendor/manufacturer. Products without a vendor land
  // in "(No vendor)" so they stay discoverable at the bottom. Search
  // filters *before* grouping so empty vendors don't show up.
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) =>
          [r.name, r.vendor, r.category, r.sku, r.notes]
            .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
        )
      : rows;
    const g = {};
    filtered.forEach((r) => {
      const v = (r.vendor || "").trim() || "(No vendor)";
      (g[v] ||= []).push(r);
    });
    // Keep each vendor's products in manual order (up/down arrows persist
    // sort_order); items never reordered fall back to alphabetical.
    Object.values(g).forEach((arr) => arr.sort((a, b) => {
      const sa = a.sort_order ?? 1e9, sb = b.sort_order ?? 1e9;
      if (sa !== sb) return sa - sb;
      return (a.name || "").localeCompare(b.name || "");
    }));
    return g;
  }, [rows, search]);

  const vendorOrder = useMemo(
    () => Object.keys(grouped).sort((a, b) => {
      if (a === "(No vendor)") return 1;
      if (b === "(No vendor)") return -1;
      return a.localeCompare(b);
    }),
    [grouped],
  );

  const toggle = (v) => setCollapsed((c) => ({ ...c, [v]: !c[v] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => setCollapsed(vendorOrder.reduce((acc, v) => ({ ...acc, [v]: true }), {}));

  return (
    <div className="space-y-4">
      {/* Toolbar: search + expand/collapse + import/add */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search product, SKU, category, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 pl-8 pr-3 border border-zinc-300 rounded-sm text-sm"
            data-testid="products-search"
          />
        </div>
        <button onClick={expandAll} data-testid="products-expand-all" className="border border-zinc-300 px-2.5 h-9 text-[10px] font-bold uppercase tracking-wider hover:border-blue-700 hover:text-blue-700 rounded-sm">Expand All</button>
        <button onClick={collapseAll} data-testid="products-collapse-all" className="border border-zinc-300 px-2.5 h-9 text-[10px] font-bold uppercase tracking-wider hover:border-blue-700 hover:text-blue-700 rounded-sm">Collapse All</button>
        <button onClick={() => setImporting(true)} className="inline-flex items-center gap-2 border border-zinc-300 px-3 h-9 text-xs font-bold uppercase tracking-wider hover:border-blue-700 hover:text-blue-700 rounded-sm" data-testid="import-csv-btn">
          <Upload className="w-3.5 h-3.5" /> Import CSV
        </button>
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-blue-700 text-white px-3 h-9 text-xs font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm" data-testid="add-product-btn">
          <Plus className="w-3.5 h-3.5" /> Add Product
        </button>
      </div>

      {rows.length === 0 && (
        <div className="text-zinc-400 text-sm text-center py-8 border border-dashed border-zinc-200 rounded-sm">
          No products yet — tap <b>Add Product</b> or <b>Import CSV</b>
        </div>
      )}
      {rows.length > 0 && vendorOrder.length === 0 && (
        <div className="text-zinc-400 text-sm text-center py-8 border border-dashed border-zinc-200 rounded-sm" data-testid="products-empty-search">
          No products match “{search}”.
        </div>
      )}

      {vendorOrder.map((vendor) => {
        const items = grouped[vendor];
        const isCollapsed = !!collapsed[vendor];
        const tone = vendorTone(vendor);
        return (
          <div key={vendor} className={`border border-zinc-200 rounded-sm overflow-hidden border-l-4 ${tone.accent.split(" ")[0]}`} data-testid={`products-vendor-${vendor.replace(/\s+/g, "-").toLowerCase()}`}>
            {/* Vendor header — click to collapse/expand. Bg tint uses the
                vendor's own light shade so each section is visually
                distinct at a glance. */}
            <button
              type="button"
              onClick={() => toggle(vendor)}
              className={`w-full flex items-center gap-2 px-4 py-2.5 hover:brightness-95 border-b border-zinc-200 text-left ${tone.accent.split(" ").slice(1).join(" ")}`}
              data-testid={`products-vendor-header-${vendor.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
              <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm ${tone.chip}`}>{vendor}</span>
              <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                {items.length} product{items.length === 1 ? "" : "s"}
              </span>
            </button>

            {!isCollapsed && (
              <table className="w-full text-sm">
                <thead className="bg-white text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                  <tr>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-left px-3 py-2">Category</th>
                    <th className="text-right px-3 py-2">Pkg</th>
                    <th className="text-left px-3 py-2">Unit</th>
                    <th className="text-right px-3 py-2">Unit Price</th>
                    <th className="text-right px-3 py-2">Pkg Price</th>
                    <th className="w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, idx) => editingId === p.id ? (
                    <React.Fragment key={p.id}>
                    <tr className="border-t border-zinc-100 bg-blue-50/40">
                      <td className="px-3 py-2"><input className={inputCls} value={draft.name||""} onChange={(e)=>setDraft({...draft,name:e.target.value})} /></td>
                      <td className="px-3 py-2"><input className={inputCls} value={draft.sku||""} onChange={(e)=>setDraft({...draft,sku:e.target.value})} /></td>
                      <td className="px-3 py-2"><select className={inputCls} value={draft.category||""} onChange={(e)=>setDraft({...draft,category:e.target.value})}><option value="">—</option>{CATEGORIES.map((c)=><option key={c} value={c}>{c}</option>)}</select></td>
                      <td className="px-3 py-2"><input type="number" step="0.01" className={inputCls + " text-right"} value={draft.package_size||""} onChange={(e)=>setDraft({...draft,package_size:e.target.value})} /></td>
                      <td className="px-3 py-2"><select className={inputCls} value={draft.unit||""} onChange={(e)=>setDraft({...draft,unit:e.target.value})}>{UNITS.map((u)=><option key={u} value={u}>{u}</option>)}</select></td>
                      <td className="px-3 py-2"><input type="number" step="0.01" className={inputCls + " text-right"} value={draft.unit_price||""} onChange={(e)=>setDraft({...draft,unit_price:e.target.value})} /></td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-500">${((parseFloat(draft.unit_price)||0) * (parseFloat(draft.package_size)||1)).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={saveEdit} className="p-1 text-emerald-700 hover:bg-emerald-50"><Save className="w-4 h-4" /></button>
                        <button onClick={cancelEdit} className="p-1 text-zinc-500 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
                      </td>
                    </tr>
                    <tr className="bg-blue-50/40">
                      <td colSpan={8} className="px-3 pb-2">
                        <input
                          className={inputCls}
                          value={draft.notes || ""}
                          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                          placeholder="Note (coverage rate, color availability, ordering info…)"
                          data-testid="product-edit-notes"
                        />
                      </td>
                    </tr>
                    </React.Fragment>
                  ) : (
                    <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={()=>startEdit(p)} data-testid={`product-row-${p.id}`}>
                      <td className="px-3 py-2 font-medium">
                        {p.name}
                        {p.notes && <div className="text-[11px] text-zinc-500 font-normal mt-0.5" data-testid={`product-note-${p.id}`}>{p.notes}</div>}
                      </td>
                      <td className="px-3 py-2 text-zinc-500 font-mono text-xs">{p.sku || <span className="text-zinc-300">—</span>}</td>
                      <td className="px-3 py-2"><span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 px-1.5 py-0.5 rounded-sm">{p.category||"—"}</span></td>
                      <td className="px-3 py-2 text-right text-zinc-600">{p.package_size}</td>
                      <td className="px-3 py-2 text-zinc-600">{p.unit}</td>
                      <td className="px-3 py-2 text-right font-mono">${(p.unit_price ?? 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-600" data-testid={`product-pkg-price-${p.id}`}>
                        ${((p.unit_price ?? 0) * (p.package_size || 1)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span className="text-[10px] text-zinc-400"> /{p.package_size > 1 ? `${p.package_size} ${p.unit}` : p.unit}</span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={(e)=>{e.stopPropagation(); moveProduct(items, idx, -1);}} disabled={idx === 0} title="Move up" className="p-1 text-zinc-500 hover:bg-zinc-100 disabled:opacity-20" data-testid={`product-up-${p.id}`}><ChevronUp className="w-4 h-4" /></button>
                        <button onClick={(e)=>{e.stopPropagation(); moveProduct(items, idx, 1);}} disabled={idx === items.length - 1} title="Move down" className="p-1 text-zinc-500 hover:bg-zinc-100 disabled:opacity-20" data-testid={`product-down-${p.id}`}><ChevronDown className="w-4 h-4" /></button>
                        <button onClick={(e)=>{e.stopPropagation(); startEdit(p);}} title="Edit" className="p-1 text-blue-700 hover:bg-blue-50" data-testid={`product-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={(e)=>{e.stopPropagation(); removeProduct(p);}} title="Delete" className="p-1 text-rose-600 hover:bg-rose-50" data-testid={`product-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
      {adding && <AddProductModal knownVendors={vendorOrder.filter((v) => v !== "(No vendor)")} onClose={()=>{setAdding(false);load();}} />}
      {importing && <ImportCsvModal onClose={()=>{setImporting(false);load();}} />}
    </div>
  );
}

function AddProductModal({ knownVendors = [], onClose }) {
  const [draft, setDraft] = useState({ name:"", vendor:knownVendors[0] || "", category:"FARM", unit:"gal", package_size:1, unit_price:0 });
  // "+ Add new vendor…" mode — swaps the vendor select for a free-text
  // input so the rep can type a brand-new manufacturer name.
  const [customVendor, setCustomVendor] = useState(false);
  // Merge Material Suppliers from the Vendors directory into the dropdown —
  // subs / equipment / dumpster vendors don't belong in a product catalog.
  const [allVendors, setAllVendors] = useState(knownVendors);
  useEffect(() => {
    api.get("/vendors")
      .then((r) => {
        const dirNames = (r.data || [])
          .filter((v) => !v.is_deleted && v.category === "Material Supplier")
          .map((v) => (v.name || v.vendor_name || "").trim())
          .filter(Boolean);
        const merged = [...new Set([...knownVendors, ...dirNames])].sort((a, b) => a.localeCompare(b));
        setAllVendors(merged);
        setDraft((d) => (d.vendor ? d : { ...d, vendor: merged[0] || "" }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = async () => {
    if (!draft.name.trim()) { toast.error("Name required"); return; }
    if (!draft.vendor.trim()) { toast.error("Vendor required"); return; }
    try { await api.post("/products", draft); toast.success("Product added"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white p-6 rounded-sm w-full max-w-md" onClick={(e)=>e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">Add Product</h2>
        <div className="space-y-3">
          <div><label className={labelCls}>Name</label><input className={inputCls} value={draft.name} onChange={(e)=>setDraft({...draft,name:e.target.value})} autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Vendor</label>
              {customVendor ? (
                <div className="flex gap-1">
                  <input className={inputCls} value={draft.vendor} onChange={(e)=>setDraft({...draft,vendor:e.target.value})} placeholder="New vendor name" autoFocus />
                  <button type="button" onClick={()=>{setCustomVendor(false); setDraft({...draft, vendor: allVendors[0] || ""});}} className="border border-zinc-300 px-2 text-xs" title="Cancel new vendor">×</button>
                </div>
              ) : (
                <select
                  className={inputCls}
                  value={draft.vendor}
                  onChange={(e) => {
                    if (e.target.value === "__new__") { setCustomVendor(true); setDraft({ ...draft, vendor: "" }); }
                    else setDraft({ ...draft, vendor: e.target.value });
                  }}
                  data-testid="add-product-vendor"
                >
                  {allVendors.length === 0 && <option value="">— No vendors yet —</option>}
                  {allVendors.map((v) => <option key={v} value={v}>{v}</option>)}
                  <option value="__new__">+ Add new vendor…</option>
                </select>
              )}
            </div>
            <div><label className={labelCls}>Category</label><select className={inputCls} value={draft.category} onChange={(e)=>setDraft({...draft,category:e.target.value})}>{CATEGORIES.map((c)=><option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className={labelCls}>Package size</label><input type="number" step="1" min="1" className={inputCls} value={draft.package_size} onChange={(e)=>setDraft({...draft,package_size:e.target.value})} /></div>
            <div><label className={labelCls}>Unit</label><select className={inputCls} value={draft.unit} onChange={(e)=>setDraft({...draft,unit:e.target.value})}>{UNITS.map((u)=><option key={u} value={u}>{u}</option>)}</select></div>
            <div className="col-span-2"><label className={labelCls}>Unit price ($ per {draft.unit || "unit"})</label><input type="number" step="0.01" className={inputCls} value={draft.unit_price} onChange={(e)=>setDraft({...draft,unit_price:e.target.value})} />
              <div className="text-[11px] text-zinc-500 mt-1">= ${(((parseFloat(draft.unit_price)||0) * (parseFloat(draft.package_size)||1))).toLocaleString(undefined,{minimumFractionDigits:2})} per {parseFloat(draft.package_size)||1} {draft.unit || "unit"} package — enter the <b>per-{draft.unit || "unit"}</b> price, the calculator prices per {draft.unit || "unit"}.</div>
            </div>
            <div className="col-span-2"><label className={labelCls}>Note (optional)</label><input className={inputCls} value={draft.notes||""} onChange={(e)=>setDraft({...draft,notes:e.target.value})} placeholder="Coverage rate, color availability, ordering info…" data-testid="add-product-notes" /></div>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="border border-zinc-300 px-3 h-9 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button onClick={submit} className="bg-blue-700 text-white px-3 h-9 text-xs font-bold uppercase tracking-wider">Add</button>
        </div>
      </div>
    </div>
  );
}

function ImportCsvModal({ onClose }) {
  const KNOWN_COLS = ["name", "sku", "vendor", "category", "unit", "package_size", "unit_price", "notes"];
  const [csv, setCsv] = useState("name,vendor,category,unit,package_size,unit_price,notes\n");
  const [defaultVendor, setDefaultVendor] = useState("");
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const preview = useMemo(() => {
    const text = csv.trim();
    if (!text) return null;
    const parseLine = (line) => {
      const out = []; let cur = "", q = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (q) {
          if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (ch === '"') q = false;
          else cur += ch;
        } else if (ch === '"') q = true;
        else if (ch === ",") { out.push(cur); cur = ""; }
        else cur += ch;
      }
      out.push(cur);
      return out;
    };
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) return null;
    const headers = parseLine(lines[0]).map((h) => h.trim().toLowerCase().replace(/ /g, "_"));
    const looksLikePriceList = headers.some((h) => ["price/sq_ft", "item_code", "uom", "sku"].includes(h));
    return {
      headers,
      unknown: headers.filter((h) => !KNOWN_COLS.includes(h)),
      hasName: headers.includes("name"),
      looksLikePriceList,
      rows: lines.slice(1, 9).map(parseLine),
      total: lines.length - 1,
    };
  }, [csv]); // eslint-disable-line
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(f);
  };
  const submit = async () => {
    try {
      const { data } = await api.post("/products/import-csv", { csv, default_vendor: defaultVendor });
      setResult(data);
      toast.success(`Imported: ${data.inserted} new, ${data.updated} updated`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white p-6 rounded-sm w-full max-w-2xl" onClick={(e)=>e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-1">Import Products from CSV</h2>
        <p className="text-xs text-zinc-600 mb-3">First row must be the header. Required: <code>name</code>. Optional: <code>sku, vendor, category, unit, package_size, unit_price, notes</code> — products are organized by name & manufacturer, so <code>category</code> (TPO, EPDM…) can be left out entirely. Existing rows with the same name+vendor get updated (no duplicates).</p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <label className="inline-flex items-center gap-2 border border-zinc-300 px-3 h-9 text-xs font-bold uppercase tracking-wider hover:border-blue-700 hover:text-blue-700 rounded-sm cursor-pointer" data-testid="import-csv-file-label">
            <Upload className="w-3.5 h-3.5" /> Choose CSV File
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="import-csv-file-input" />
          </label>
          {fileName && <span className="text-xs text-zinc-600 font-mono" data-testid="import-csv-file-name">{fileName}</span>}
          <input
            type="text"
            placeholder="Manufacturer for all rows (e.g. GAF) — optional"
            value={defaultVendor}
            onChange={(e) => setDefaultVendor(e.target.value)}
            className="h-9 px-3 border border-zinc-300 rounded-sm text-sm flex-1 min-w-[220px]"
            data-testid="import-csv-default-vendor"
          />
        </div>
        <textarea data-testid="import-csv-textarea" className="w-full h-40 border border-zinc-300 p-2 font-mono text-xs" value={csv} onChange={(e)=>setCsv(e.target.value)} />
        {preview && preview.rows.length > 0 && (
          <div className="mt-3" data-testid="import-csv-preview">
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
              Preview — first {preview.rows.length} of {preview.total} rows
            </div>
            {preview.looksLikePriceList && (
              <div className="text-xs text-rose-700 font-bold mb-1 border border-rose-300 bg-rose-50 rounded-sm px-2 py-1.5" data-testid="import-csv-pricelist-warning">
                This looks like a distributor price list (TPO/EPDM/PVC…). Import it under Single-Ply Est. → Price Lists instead — this catalog is for coating materials.
              </div>
            )}
            {!preview.hasName && (
              <div className="text-xs text-rose-600 font-bold mb-1" data-testid="import-csv-no-name-warning">
                Missing required “name” column — check the header row.
              </div>
            )}
            {preview.unknown.length > 0 && (
              <div className="text-xs text-amber-700 mb-1" data-testid="import-csv-unknown-warning">
                Ignored column{preview.unknown.length > 1 ? "s" : ""}: <b>{preview.unknown.join(", ")}</b> (not a recognized field)
              </div>
            )}
            <div className="overflow-x-auto border border-zinc-200 rounded-sm max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left bg-zinc-50 border-b border-zinc-200">
                    {preview.headers.map((h, i) => (
                      <th key={i} className={`px-2 py-1.5 font-mono font-bold whitespace-nowrap ${KNOWN_COLS.includes(h) ? "text-zinc-700" : "text-amber-600 line-through"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, ri) => (
                    <tr key={ri} className="border-b border-zinc-50">
                      {preview.headers.map((_, ci) => (
                        <td key={ci} className="px-2 py-1 whitespace-nowrap text-zinc-600">{r[ci] ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {result && (
          <div className="mt-2 text-xs" data-testid="import-csv-result">
            <b>Done.</b> {result.inserted} inserted, {result.updated} updated.
            {result.errors?.length > 0 && <span className="text-rose-600"> {result.errors.length} errors — check line numbers.</span>}
          </div>
        )}
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="border border-zinc-300 px-3 h-9 text-xs font-bold uppercase tracking-wider" data-testid="import-csv-close">Close</button>
          <button onClick={submit} className="bg-blue-700 text-white px-3 h-9 text-xs font-bold uppercase tracking-wider" data-testid="import-csv-submit">Import</button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────── SYSTEMS ───────────────────────
function SystemsTab() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null); // system object

  const load = async () => {
    try { setRows((await api.get("/systems")).data); }
    catch { toast.error("Failed to load systems"); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (sys) => {
    if (!window.confirm(`Delete system "${sys.name}" and its entire recipe?`)) return;
    await api.delete(`/systems/${sys.id}`);
    load();
  };

  // Group by vendor (the vendor determines the system, per Darren). Systems
  // missing a vendor land in a "(No vendor)" bucket at the end so they're
  // still discoverable.
  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const v = (r.vendor || "(No vendor)").trim() || "(No vendor)";
      (g[v] ||= []).push(r);
    });
    return g;
  }, [rows]);
  const vendorOrder = useMemo(
    () => Object.keys(grouped).sort((a, b) => {
      if (a === "(No vendor)") return 1;
      if (b === "(No vendor)") return -1;
      return a.localeCompare(b);
    }),
    [grouped],
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={()=>setAdding(true)} className="inline-flex items-center gap-2 bg-blue-700 text-white px-3 h-9 text-xs font-bold uppercase tracking-wider hover:bg-blue-800" data-testid="add-system-btn">
          <Plus className="w-3.5 h-3.5" /> Add System
        </button>
      </div>
      {rows.length === 0 && <div className="text-zinc-400 text-sm text-center py-8 border border-dashed border-zinc-200">No systems yet — tap <b>Add System</b></div>}
      {vendorOrder.map((vendor) => {
        const tone = vendorTone(vendor);
        return (
        <div key={vendor}>
          <div className={`flex items-center gap-2 mb-2 border-l-4 pl-2 py-1 ${tone.accent}`}>
            <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm ${tone.chip}`}>{vendor}</span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              {grouped[vendor].length} system{grouped[vendor].length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            {grouped[vendor].map((s, i) => (
              <div key={s.id} className={`flex items-center justify-between px-4 py-3 hover:bg-zinc-50 cursor-pointer ${i>0 ? "border-t border-zinc-200" : ""}`} onClick={()=>setEditingRecipe(s)} data-testid={`system-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {s.name}
                    {s.system_type && <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-sm">{s.system_type}</span>}
                    {s.warranty_years ? <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-sm">{s.warranty_years}-yr warranty</span> : null}
                  </div>
                  {s.description && <div className="text-xs text-zinc-500 mt-0.5">{s.description}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Edit recipe</span>
                  <ChevronRight className="w-4 h-4 text-zinc-400" />
                  <button onClick={(e)=>{e.stopPropagation();remove(s);}} className="p-1 text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      );})}
      {adding && <AddSystemModal onClose={()=>{setAdding(false);load();}} />}
      {editingRecipe && <RecipeEditor system={editingRecipe} onClose={()=>{setEditingRecipe(null);load();}} />}
    </div>
  );
}

function AddSystemModal({ onClose }) {
  const [draft, setDraft] = useState({ name:"", vendor:"", system_type:"FARM", warranty_years:10, description:"" });
  const submit = async () => {
    if (!draft.name.trim()) { toast.error("System name required"); return; }
    if (!draft.vendor.trim()) { toast.error("Vendor required — vendor determines the system"); return; }
    try { await api.post("/systems", draft); toast.success("System added"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white p-6 rounded-sm w-full max-w-md" onClick={(e)=>e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-1">Add System</h2>
        <p className="text-xs text-zinc-600 mb-4">A system is uniquely defined by <b>Vendor</b> + <b>Type</b> + <b>Warranty Period</b>. Coverage rates on the recipe change with the warranty.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Vendor *</label><input className={inputCls} value={draft.vendor} onChange={(e)=>setDraft({...draft,vendor:e.target.value})} placeholder="e.g., Gaco, Carlisle, GAF" /></div>
            <div><label className={labelCls}>System type</label><select className={inputCls} value={draft.system_type} onChange={(e)=>setDraft({...draft,system_type:e.target.value})}>{CATEGORIES.map((c)=><option key={c} value={c}>{c}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Warranty (years)</label><input type="number" min="0" className={inputCls} value={draft.warranty_years} onChange={(e)=>setDraft({...draft,warranty_years:parseInt(e.target.value)||0})} placeholder="10, 15, 20…" /></div>
            <div><label className={labelCls}>System name</label><input className={inputCls} value={draft.name} onChange={(e)=>setDraft({...draft,name:e.target.value})} autoFocus placeholder="e.g., S20 Silicone 20-yr" /></div>
          </div>
          <div><label className={labelCls}>Description (optional)</label><input className={inputCls} value={draft.description} onChange={(e)=>setDraft({...draft,description:e.target.value})} /></div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="border border-zinc-300 px-3 h-9 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button onClick={submit} className="bg-blue-700 text-white px-3 h-9 text-xs font-bold uppercase tracking-wider">Add</button>
        </div>
      </div>
    </div>
  );
}

function RecipeEditor({ system, onClose }) {
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [scopeNotes, setScopeNotes] = useState(system.scope_notes || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, rRes] = await Promise.all([
          api.get("/products"),
          api.get(`/systems/${system.id}/recipe`),
        ]);
        setProducts(pRes.data);
        setItems(rRes.data);
      } catch { toast.error("Failed to load recipe"); }
      finally { setLoading(false); }
    })();
  }, [system.id]);

  // Group products by vendor so the picker uses <optgroup> — MUCH easier to
  // scan 73+ items when they're broken out by manufacturer. The system's
  // own vendor floats to the top so the natural picks are one glance away.
  const productGroups = useMemo(() => {
    const g = {};
    products.forEach((p) => {
      const v = (p.vendor || "").trim() || "(No vendor)";
      (g[v] ||= []).push(p);
    });
    Object.values(g).forEach((arr) => arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    const vendors = Object.keys(g).sort((a, b) => {
      if (a === "(No vendor)") return 1;
      if (b === "(No vendor)") return -1;
      // Bubble the current system's vendor to the top of the picker.
      if (a === system.vendor) return -1;
      if (b === system.vendor) return 1;
      return a.localeCompare(b);
    });
    return vendors.map((v) => ({ vendor: v, items: g[v] }));
  }, [products, system.vendor]);

  const addRow = () => setItems((arr) => [...arr, { product_id:"", coverage_rate:0, coverage_basis:"per_100sf", optional:false, default_included:true, notes:"" }]);
  const updateRow = (idx, patch) => setItems((arr) => arr.map((it,i)=>i===idx?{...it,...patch}:it));
  const removeRow = (idx) => setItems((arr) => arr.filter((_,i)=>i!==idx));

  const save = async () => {
    try {
      await api.put(`/systems/${system.id}/recipe`, { items });
      await api.patch(`/systems/${system.id}`, { scope_notes: scopeNotes });
      toast.success("Recipe saved");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-5xl h-[85vh] flex flex-col" onClick={(e)=>e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Recipe — {system.name}</h2>
            <p className="text-xs text-zinc-600">Add each product in this system + its coverage rate. Mark optional add-ons (walk pad, granules) to make them togglable in the calculator.</p>
          </div>
          <button onClick={onClose} className="p-1 text-zinc-500 hover:bg-zinc-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto p-6">
          {loading ? <div className="text-center py-8 text-zinc-400">Loading…</div> : (
            <div className="space-y-2">
              {items.length === 0 && <div className="text-center text-zinc-400 py-8 text-sm border border-dashed border-zinc-200">No products in this recipe yet</div>}
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 border border-zinc-200 rounded-sm">
                  <div className="col-span-4">
                    <label className={labelCls}>Product</label>
                    <select className={inputCls} value={it.product_id} onChange={(e)=>updateRow(idx,{product_id:e.target.value})} data-testid={`recipe-product-picker-${idx}`}>
                      <option value="">— Select product —</option>
                      {productGroups.map((grp) => (
                        <optgroup key={grp.vendor} label={grp.vendor}>
                          {grp.items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Coverage rate</label>
                    <input type="number" step="0.01" className={inputCls} value={it.coverage_rate} onChange={(e)=>updateRow(idx,{coverage_rate:parseFloat(e.target.value)||0})} />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Basis</label>
                    <select className={inputCls} value={it.coverage_basis} onChange={(e)=>updateRow(idx,{coverage_basis:e.target.value})}>{COVERAGE_BASIS.map((b)=><option key={b.value} value={b.value}>{b.label}</option>)}</select>
                  </div>
                  <div className="col-span-3 flex items-end gap-3 pb-1">
                    <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={it.optional} onChange={(e)=>updateRow(idx,{optional:e.target.checked})} /> Optional</label>
                    <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={it.default_included} onChange={(e)=>updateRow(idx,{default_included:e.target.checked})} /> Default ON</label>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button onClick={()=>removeRow(idx)} className="p-1 text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              <button onClick={addRow} className="inline-flex items-center gap-2 border border-zinc-300 px-3 h-9 text-xs font-bold uppercase tracking-wider hover:border-blue-700 hover:text-blue-700 mt-2"><Plus className="w-3.5 h-3.5" /> Add row</button>
              <div className="pt-4 mt-4 border-t border-zinc-200">
                <label className={labelCls}>Scope Notes — custom wording for this system (one bullet per line)</label>
                <textarea
                  value={scopeNotes}
                  onChange={(e) => setScopeNotes(e.target.value)}
                  rows={4}
                  placeholder={"e.g. Power-wash entire roof surface prior to application\nApply per manufacturer spec sheet 123-A"}
                  data-testid="system-scope-notes"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 font-mono"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  Prints automatically as extra bullets under this system's application section on the customer Scope PDF and the Work Order.
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="border border-zinc-300 px-3 h-9 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button onClick={save} className="bg-blue-700 text-white px-3 h-9 text-xs font-bold uppercase tracking-wider">Save Recipe</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────── CALCULATOR SETTINGS ──────────────────
function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { (async () => {
    try { setS((await api.get("/calculator/settings")).data); }
    catch { toast.error("Failed to load settings"); }
  })(); }, []);
  const save = async () => {
    try { setS((await api.put("/calculator/settings", s)).data); toast.success("Settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (!s) return <div className="text-center py-8 text-zinc-400">Loading…</div>;
  return (
    <div className="max-w-xl space-y-4">
      <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Shipping %</label><input type="number" step="0.01" className={inputCls} value={s.markup_pct} onChange={(e)=>setS({...s,markup_pct:parseFloat(e.target.value)||0})} /></div>
          <div><label className={labelCls}>Handling fee %</label><input type="number" step="0.01" className={inputCls} value={s.handling_pct} onChange={(e)=>setS({...s,handling_pct:parseFloat(e.target.value)||0})} /></div>
        </div>
        <div>
          <label className={labelCls}>Handling fee applied to</label>
          <select className={inputCls} value={s.handling_basis} onChange={(e)=>setS({...s,handling_basis:e.target.value})}>
            <option value="marked_up">Marked-up total (raw × 1.15, then × 1.10)</option>
            <option value="raw">Raw cost only (raw × 1.10), shipping added separately</option>
          </select>
        </div>
        <div><label className={labelCls}>Default waste factor %</label><input type="number" step="0.01" className={inputCls} value={s.waste_pct} onChange={(e)=>setS({...s,waste_pct:parseFloat(e.target.value)||0})} /></div>
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-200">
          <div><label className={labelCls}>Overhead %</label><input type="number" step="0.01" data-testid="settings-overhead-pct" className={inputCls} value={s.overhead_pct ?? 10} onChange={(e)=>setS({...s,overhead_pct:parseFloat(e.target.value)||0})} /></div>
          <div><label className={labelCls}>Profit %</label><input type="number" step="0.01" data-testid="settings-profit-pct" className={inputCls} value={s.profit_pct ?? 10} onChange={(e)=>setS({...s,profit_pct:parseFloat(e.target.value)||0})} /></div>
        </div>
        <div className="flex justify-end">
          <button onClick={save} className="bg-blue-700 text-white px-3 h-9 text-xs font-bold uppercase tracking-wider">Save Settings</button>
        </div>
      </div>
      <div className="text-xs text-zinc-500 border border-zinc-200 p-3 rounded-sm bg-zinc-50">
        <b>How the math works:</b> Raw material cost = Σ(qty × unit price). Shipping adds {s.markup_pct}% (covers freight from the vendor to the jobsite). Handling fee adds {s.handling_pct}% {s.handling_basis === "marked_up" ? "on the shipping-included total" : "on the raw cost only"}. Then per-option Labor + Warranty are added, then Overhead ({s.overhead_pct ?? 10}%) and Profit ({s.profit_pct ?? 10}%) compound on top.
      </div>
    </div>
  );
}
